import { supabase } from './supabase';
import { BaseService } from './base-service';

/**
 * Interface representing a notification
 */
export interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type?: string;
  is_read: boolean;
  related_entity_type?: string;
  related_entity_id?: string;
  created_at?: string;
}

/**
 * Interface representing user notification preferences
 */
export interface UserPreferences {
  id: string;
  user_id: string;
  notification_new_inquiries: boolean;
  notification_new_orders: boolean;
  notification_marketing: boolean;
  privacy_profile_visibility: boolean;
  privacy_contact_visibility: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Service for managing notifications and user preferences
 */
export class NotificationService extends BaseService {
  constructor() {
    super(supabase);
  }
  
  /**
   * Get notifications for a user
   * @param userId The user ID
   * @param limit Maximum number of notifications to return
   * @param offset Offset for pagination
   * @returns Array of notifications
   */
  async getUserNotifications(userId: string, limit = 10, offset = 0): Promise<Notification[]> {
    try {
      const { data, error } = await this.supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
        
      if (error) throw error;
      return data || [];
    } catch (error) {
      this.handleError(error, 'fetching user notifications');
      return [];
    }
  }
  
  /**
   * Get unread notification count for a user
   * @param userId The user ID
   * @returns Number of unread notifications
   */
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const { count, error } = await this.supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);
        
      if (error) throw error;
      return count || 0;
    } catch (error) {
      this.handleError(error, 'fetching unread notification count');
      return 0;
    }
  }
  
  /**
   * Mark a notification as read
   * @param notificationId The notification ID
   * @returns True if successful, false otherwise
   */
  async markAsRead(notificationId: string): Promise<boolean> {
    try {
      const { error } = await this.supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
        
      if (error) throw error;
      return true;
    } catch (error) {
      this.handleError(error, 'marking notification as read');
      return false;
    }
  }
  
  /**
   * Mark all notifications as read for a user
   * @param userId The user ID
   * @returns True if successful, false otherwise
   */
  async markAllAsRead(userId: string): Promise<boolean> {
    try {
      const { error } = await this.supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);
        
      if (error) throw error;
      return true;
    } catch (error) {
      this.handleError(error, 'marking all notifications as read');
      return false;
    }
  }
  
  /**
   * Create a new notification
   * @param notification The notification data
   * @returns The created notification or null if creation failed
   */
  async createNotification(notification: Omit<Notification, 'id' | 'created_at'>): Promise<Notification | null> {
    try {
      // Check user preferences before creating notification
      const shouldNotify = await this.shouldSendNotification(
        notification.user_id,
        notification.type || ''
      );
      
      if (!shouldNotify) {
        console.log(`User ${notification.user_id} has disabled notifications for type ${notification.type}`);
        return null;
      }
      
      const { data, error } = await this.supabase
        .from('notifications')
        .insert({
          ...notification,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'creating notification');
      return null;
    }
  }
  
  /**
   * Get user preferences
   * @param userId The user ID
   * @returns User preferences or default preferences if not found
   */
  async getUserPreferences(userId: string): Promise<UserPreferences | null> {
    try {
      const { data, error } = await this.supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();
        
      if (error) {
        // If no preferences exist, create default preferences
        if (error.code === 'PGRST116') {
          return this.createDefaultPreferences(userId);
        }
        throw error;
      }
      
      return data;
    } catch (error) {
      this.handleError(error, 'fetching user preferences');
      return null;
    }
  }
  
  /**
   * Update user preferences
   * @param userId The user ID
   * @param preferences The preferences to update
   * @returns The updated preferences or null if update failed
   */
  async updateUserPreferences(userId: string, preferences: Partial<UserPreferences>): Promise<UserPreferences | null> {
    try {
      // Check if preferences exist
      const existingPrefs = await this.getUserPreferences(userId);
      
      if (!existingPrefs) {
        // Create with provided preferences
        return this.createDefaultPreferences(userId, preferences);
      }
      
      // Update existing preferences
      const { data, error } = await this.supabase
        .from('user_preferences')
        .update({
          ...preferences,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'updating user preferences');
      return null;
    }
  }
  
  /**
   * Create default preferences for a user
   * @param userId The user ID
   * @param overrides Optional preference overrides
   * @returns The created preferences or null if creation failed
   */
  private async createDefaultPreferences(
    userId: string, 
    overrides: Partial<UserPreferences> = {}
  ): Promise<UserPreferences | null> {
    try {
      const defaultPrefs = {
        user_id: userId,
        notification_new_inquiries: true,
        notification_new_orders: true,
        notification_marketing: true,
        privacy_profile_visibility: true,
        privacy_contact_visibility: false,
        ...overrides
      };
      
      const { data, error } = await this.supabase
        .from('user_preferences')
        .insert(defaultPrefs)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    } catch (error) {
      this.handleError(error, 'creating default preferences');
      return null;
    }
  }
  
  /**
   * Check if a notification should be sent based on user preferences
   * @param userId The user ID
   * @param notificationType The notification type
   * @returns True if notification should be sent, false otherwise
   */
  async shouldSendNotification(userId: string, notificationType: string): Promise<boolean> {
    try {
      const prefs = await this.getUserPreferences(userId);
      if (!prefs) return true; // Default to sending if no preferences
      
      switch (notificationType) {
        case 'inquiry':
          return prefs.notification_new_inquiries;
        case 'order':
          return prefs.notification_new_orders;
        case 'marketing':
          return prefs.notification_marketing;
        default:
          return true; // Default to sending for unknown types
      }
    } catch (error) {
      this.handleError(error, 'checking notification preferences');
      return true; // Default to sending on error
    }
  }
  
  /**
   * Send a notification for a new inquiry
   * @param userId The user ID to notify
   * @param inquiryId The inquiry ID
   * @param inquiryTitle The inquiry title or subject
   * @returns The created notification or null if creation failed
   */
  async sendInquiryNotification(userId: string, inquiryId: string, inquiryTitle: string): Promise<Notification | null> {
    return this.createNotification({
      user_id: userId,
      title: 'New Inquiry Received',
      message: `You have received a new inquiry: ${inquiryTitle}`,
      type: 'inquiry',
      is_read: false,
      related_entity_type: 'inquiry',
      related_entity_id: inquiryId
    });
  }
  
  /**
   * Send a notification for a new order
   * @param userId The user ID to notify
   * @param orderId The order ID
   * @param orderNumber The order number
   * @returns The created notification or null if creation failed
   */
  async sendOrderNotification(userId: string, orderId: string, orderNumber: string): Promise<Notification | null> {
    return this.createNotification({
      user_id: userId,
      title: 'New Order Received',
      message: `You have received a new order: #${orderNumber}`,
      type: 'order',
      is_read: false,
      related_entity_type: 'order',
      related_entity_id: orderId
    });
  }
  
  /**
   * Send a marketing notification
   * @param userId The user ID to notify
   * @param title The notification title
   * @param message The notification message
   * @returns The created notification or null if creation failed
   */
  async sendMarketingNotification(userId: string, title: string, message: string): Promise<Notification | null> {
    return this.createNotification({
      user_id: userId,
      title,
      message,
      type: 'marketing',
      is_read: false
    });
  }
}
